import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Reveal, KineticLines, Stagger, staggerItem } from "@/components/motion/Reveal";
import SmartImg from "@/components/SmartImg";

const HERO = "/media/boncoco-bg.png";

const REASONS = [
  { id: "Performa konsisten batch-ke-batch", en: "Consistent batch-to-batch performance" },
  { id: "Portofolio natural & alkalized", en: "Natural & alkalized portfolio" },
  { id: "Rentang warna & pH yang luas", en: "Wide colour & pH range" },
  { id: "Pasokan yang andal", en: "Reliable supply" },
  { id: "Cocok untuk bakery, minuman, confectionery & dairy", en: "Suited across bakery, beverages, confectionery & dairy" },
];

export default function Clara() {
  const { loc } = useLang();
  const { data: grades = [] } = useQuery({
    queryKey: ["cocoa-grades"],
    queryFn: async () => (await api.get("/cocoa-grades")).data,
  });

  return (
    <div className="bg-clara-bg text-clara-text" data-testid="clara-page">
      {/* HERO */}
      <section className="relative min-h-[92svh] flex items-end overflow-hidden">
        <SmartImg src={HERO} alt="Cocoa" priority className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-clara-bg via-clara-bg/70 to-clara-bg/30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 pb-16 md:pb-24 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <SmartImg src="/logos/clara-light.png" alt="Clara" priority className="h-16 md:h-20 max-w-[300px] object-contain mb-8" />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-clara-accent" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-clara-accent">Clara · Cocoa Powder</span>
          </motion.div>
          <h1 className="font-serif text-[13vw] md:text-[8.5vw] lg:text-[9rem] leading-[0.86] text-clara-text">
            <KineticLines lines={["Crafting Cocoa", "Excellence."]} stagger={0.14} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-8 max-w-2xl text-clara-text/70 text-lg leading-relaxed">
            {loc({ id: "Kami mengembangkan cokelat bubuk yang dirancang untuk warna, rasa, dan fungsi yang konsisten di berbagai aplikasi pangan.", en: "We develop cocoa powders engineered for consistent colour, flavour, and functionality across a wide range of food applications." })}
          </motion.p>
        </div>
      </section>

      {/* WHY CLARA */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-clara-accent font-semibold mb-4">Why Clara</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] text-clara-text">
              {loc({ id: "Mengapa produsen memilih Clara.", en: "Why manufacturers choose Clara." })}
            </h2>
          </Reveal>
          <div className="lg:col-span-7">
            <Stagger className="divide-y divide-clara-text/10 border-t border-clara-text/10">
              {REASONS.map((r, i) => (
                <motion.div key={i} variants={staggerItem} className="flex items-start gap-5 py-6">
                  <span className="mt-1 h-6 w-6 shrink-0 rounded-full bg-clara/30 text-clara-accent flex items-center justify-center"><Check size={14} /></span>
                  <span className="text-xl md:text-2xl font-serif text-clara-text/90">{loc(r)}</span>
                </motion.div>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* GRADE GRID */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24 md:pb-36">
        <Reveal className="mb-12">
          <h2 className="font-serif text-4xl md:text-6xl text-clara-text">{loc({ id: "Sembilan Grade", en: "Nine Grades" })}</h2>
          <p className="text-clara-text/60 mt-4 max-w-xl leading-relaxed">{loc({ id: "Dari cokelat muda hingga hitam pekat — telusuri seluruh spektrum warna dan pH kami. Dapat disesuaikan sesuai permintaan.", en: "From light brown to deep black — browse our full spectrum of colour and pH. Can be customized on request." })}</p>
        </Reveal>

        <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5">
          {grades.map((g) => (
            <motion.div key={g.grade} variants={staggerItem}>
              <Link
                to={`/brands/clara/products/${g.grade}`}
                data-testid={`grade-card-${g.grade}`}
                className="group block bg-clara-surface border border-clara-text/10 hover:border-clara-accent transition-colors duration-300"
              >
                <div className="h-40 md:h-52 w-full relative overflow-hidden bg-clara-surface">
                  <SmartImg src={g.image_url} alt="" className="h-full w-full object-cover" />
                  <div className={`absolute inset-0 mix-blend-multiply ${g.real_photo ? "opacity-0" : "opacity-70"}`} style={{ background: g.swatch }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 font-serif text-3xl md:text-4xl text-white drop-shadow">{g.grade}</div>
                </div>
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-clara-text/80">{loc(g.color)}</div>
                    <div className="text-[11px] uppercase tracking-wider text-clara-text/50">pH {g.ph}</div>
                  </div>
                  <ArrowRight size={18} className="text-clara-accent opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                </div>
              </Link>
            </motion.div>
          ))}
        </Stagger>
        <p className="mt-8 text-sm text-clara-text/50 italic">* Can be customized on request.</p>
      </section>

      {/* CERT + CTA */}
      <section className="border-t border-clara-text/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-wrap gap-x-10 gap-y-4 mb-16">
            {["Halal", "ISO 9001:2015", "SNI", "BPOM"].map((c) => (
              <span key={c} className="font-serif text-2xl md:text-3xl text-clara-accent">{c}</span>
            ))}
          </div>
          <Reveal>
            <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-4xl">
              {loc({ id: "Ceritakan apa yang Anda buat.", en: "Tell us what you're making." })}
            </h2>
            <p className="text-clara-text/70 max-w-2xl mt-8 text-lg leading-relaxed">
              {loc({ id: "Kirim spesifikasi, aplikasi, dan volume Anda — kami akan membalas dengan sampel.", en: "Reach out with your specification, application, and volume — we'll reply with a sample." })}
            </p>
            <Link to="/contact?type=export" data-testid="clara-cta-btn" className="group inline-flex items-center gap-2 bg-clara text-white rounded-none px-8 py-4 mt-10 text-sm font-semibold uppercase tracking-widest hover:bg-[#8A362B] transition-colors duration-300">
              {loc({ id: "Ajukan Pertanyaan Ekspor", en: "Make an Export Inquiry" })} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
